import React, { useState } from "react";

const SECRET_PASSWORD = "charcoocherie"; // <-- Set your password here
const SECRET_PHRASE = "Georgina"; // <-- The phrase for the hidden text box
const KONAMI_CODE = [
  "ArrowUp", "ArrowUp",
  "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight",
  "ArrowLeft", "ArrowRight",
  "b", "a",
  "Enter" // <-- Add Enter as the final step
];

interface PasswordPromptProps {
  onSuccess: () => void;
}

const PasswordPrompt: React.FC<PasswordPromptProps> = ({ onSuccess }) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [showHeart, setShowHeart] = useState(false);
  const [showFirstHint, setShowFirstHint] = useState(false);
  const [showHiddenBox, setShowHiddenBox] = useState(false);
  const [hiddenInput, setHiddenInput] = useState("");
  const [showSecondHint, setShowSecondHint] = useState(false);
  const [showClickablePasswordHint, setShowClickablePasswordHint] = useState(false);
  const [showFinalHint, setShowFinalHint] = useState(false);
  const [showKeyboardHint, setShowKeyboardHint] = useState(false);
  const [konamiProgress, setKonamiProgress] = useState(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input === SECRET_PASSWORD) {
      onSuccess();
    } else {
      setError("Incorrect password. Please try again!");
      setShowHeart(true);
    }
  };

  // When first hint is shown, also show the hidden box
  React.useEffect(() => {
    if (showFirstHint) setShowHiddenBox(true);
  }, [showFirstHint]);

  // Handle hidden box input
  const handleHiddenInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHiddenInput(e.target.value);
    if (e.target.value.trim().toLowerCase() === SECRET_PHRASE.toLowerCase()) {
      setShowKeyboardHint(true); // Only show the Konami hint
      setShowSecondHint(false);  // Hide the "click something" hint for now
    }
  };

  React.useEffect(() => {
    if (!showKeyboardHint) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === KONAMI_CODE[konamiProgress].toLowerCase()) {
        setKonamiProgress(prev => prev + 1);
        if (konamiProgress + 1 === KONAMI_CODE.length) {
          setShowClickablePasswordHint(true);
          setShowSecondHint(true); // Show the "Maybe try clicking..." hint
          setKonamiProgress(0);
          // Do NOT setShowKeyboardHint(false);
        }
      } else {
        setKonamiProgress(0); // reset if wrong key
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showKeyboardHint, konamiProgress]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Quicksand', 'Fredoka', sans-serif",
        position: "relative"
      }}
    >
      {/* Heart in the top right corner, appears after incorrect attempt */}
      {showHeart && !showFirstHint && (
        <span
          style={{
            position: "fixed",
            top: 32,
            right: 32,
            fontSize: 32,
            cursor: "pointer",
            userSelect: "none",
            zIndex: 10,
            transition: "transform 0.2s, opacity 0.3s",
            opacity: 0.45,
            filter: "drop-shadow(0 2px 8px #d16ba533)"
          }}
          title="Need a hint?"
          onMouseOver={e => {
            e.currentTarget.style.transform = "scale(1.2)";
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.opacity = "0.45";
          }}
          onClick={() => setShowFirstHint(true)}
          tabIndex={0}
          role="button"
          aria-label="Show hint"
        >
          💖
        </span>
      )}

      {/* First hint box, appears near the heart */}
      {showFirstHint && (
        <div
          style={{
            position: "fixed",
            top: 80,
            right: 40,
            background: "#f3e8ff",
            color: "#7e57c2",
            borderRadius: 10,
            padding: "12px 18px",
            fontSize: 16,
            boxShadow: "0 2px 8px #d16ba522",
            animation: "fadeInHint 0.4s",
            zIndex: 11,
            maxWidth: 260
          }}
        >
          <span style={{ fontWeight: 600 }}>Hint:</span> There is a hidden text box somewhere on the page. Can you find it?
          <style>
            {`
              @keyframes fadeInHint {
                from { opacity: 0; transform: translateY(10px);}
                to { opacity: 1; transform: translateY(0);}
              }
            `}
          </style>
        </div>
      )}

      {/* Hidden text box in the top left */}
      {showHiddenBox && (
        <input
          type="text"
          value={hiddenInput}
          onChange={handleHiddenInput}
          placeholder="Where are we right now?"
          style={{
            position: "fixed",
            top: 28,
            left: 28,
            opacity: 0.12,
            background: "#fff",
            border: "2px solid #b3e5fc",
            borderRadius: 10,
            padding: "6px 14px",
            fontSize: 16,
            fontFamily: "'Quicksand', 'Fredoka', sans-serif",
            zIndex: 12,
            transition: "opacity 0.3s, border-color 0.2s",
            outline: "none",
            boxShadow: "0 2px 8px #b3eaff22"
          }}
          onFocus={e => (e.currentTarget.style.opacity = "1")}
          onBlur={e => (e.currentTarget.style.opacity = "0.12")}
        />
      )}

      {/* Only one hint below the hidden input at a time */}
      {showKeyboardHint && (
        <div
          style={{
            position: "fixed",
            top: 62,
            left: 28,
            background: "#f3e8ff",
            color: "#7e57c2",
            borderRadius: 10,
            padding: "12px 18px",
            fontSize: 16,
            boxShadow: "0 2px 8px #d16ba522",
            animation: "fadeInHint 0.4s",
            zIndex: 13,
            maxWidth: 260
          }}
        >
          <span style={{ fontWeight: 600 }}>Hint:</span> Try entering a famous video game code on your keyboard...
          <div style={{ marginTop: 10, display: "flex", gap: 4 }}>
            {KONAMI_CODE.map((_, idx) => (
              <span
                key={idx}
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: idx < konamiProgress ? "#d16ba5" : "#b3e5fc",
                  opacity: idx < konamiProgress ? 1 : 0.4,
                  transition: "background 0.2s, opacity 0.2s"
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* "Maybe try clicking..." hint appears above the main card */}
      {showSecondHint && (
        <div
          style={{
            position: "fixed",
            bottom: 40,
            left: 0,
            right: 0,
            margin: "0 auto",
            background: "#f3e8ff",
            color: "#7e57c2",
            borderRadius: 10,
            padding: "12px 18px",
            fontSize: 16,
            boxShadow: "0 2px 8px #d16ba522",
            animation: "fadeInHint 0.4s",
            zIndex: 30,
            maxWidth: 320,
            textAlign: "center"
          }}
        >
          <span style={{ fontWeight: 600 }}>Hint:</span> Maybe try clicking on something you wouldn’t expect…
        </div>
      )}

      {/* Main password prompt card */}
      <div
        style={{
          background: "#fff",
          borderRadius: 24,
          boxShadow: "0 4px 24px #b3eaff33",
          padding: "48px 32px 36px 32px",
          textAlign: "center",
          maxWidth: 350,
          width: "100%",
          position: "relative"
        }}
      >
        <h2
          style={{
            color: "#d16ba5",
            fontWeight: 700,
            marginBottom: 16,
            fontFamily: "'Fredoka', 'Quicksand', sans-serif",
            // No pointer or click on the whole h2
            cursor: "default",
            textDecoration: "none",
            transition: "color 0.2s"
          }}
          aria-label="Secret clickable password"
        >
          Enter the secret{" "}
          <span
            style={{
              fontWeight: 900,
              cursor: showClickablePasswordHint ? "pointer" : "default",
              // No underline, no color change
              textDecoration: "none",
              transition: "color 0.2s"
            }}
            onClick={() => {
              if (showClickablePasswordHint) setShowFinalHint(true);
            }}
            tabIndex={showClickablePasswordHint ? 0 : -1}
            aria-label="Secret clickable password"
          >
            password
          </span>{" "}
          to begin!
        </h2>
        <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
          <input
            type="text"
            value={input}
            onChange={e => {
              setInput(e.target.value);
              setError("");
            }}
            placeholder="Secret password"
            style={{
              fontSize: 20,
              padding: "10px 16px",
              borderRadius: 14,
              border: "2px solid #b3e5fc",
              outline: "none",
              fontFamily: "'Quicksand', sans-serif",
              background: "#f8fbff",
              marginBottom: 12,
              width: "100%",
              boxSizing: "border-box",
              transition: "border-color 0.2s"
            }}
            onFocus={e => (e.currentTarget.style.borderColor = "#d16ba5")}
            onBlur={e => (e.currentTarget.style.borderColor = "#b3e5fc")}
          />
          <button
            type="submit"
            style={{
              marginLeft: 0,
              marginTop: 8,
              fontSize: 20,
              padding: "10px 28px",
              borderRadius: 16,
              border: "none",
              background: "linear-gradient(90deg, #d16ba5 0%, #86a8e7 100%)",
              color: "#fff",
              fontWeight: 700,
              fontFamily: "'Quicksand', 'Fredoka', sans-serif",
              cursor: "pointer",
              boxShadow: "0 2px 8px #d16ba522",
              transition: "background 0.2s, transform 0.15s"
            }}
            onMouseOver={e => (e.currentTarget.style.transform = "scale(1.07)")}
            onMouseOut={e => (e.currentTarget.style.transform = "scale(1)")}
          >
            Submit
          </button>
        </form>
        {error && <div style={{ color: "#e57373", marginBottom: 10, fontWeight: 500 }}>{error}</div>}
        {showFinalHint && (
          <div
            style={{
              marginTop: 18,
              background: "#ffe0f7",
              color: "#c2185b",
              borderRadius: 10,
              padding: "12px 18px",
              fontSize: 16,
              boxShadow: "0 2px 8px #d16ba522",
              animation: "fadeInHint 0.4s",
              maxWidth: 260,
              marginLeft: "auto",
              marginRight: "auto"
            }}
          >
            <span style={{ fontWeight: 600 }}>Final Hint:</span> The answer is hidden somewhere in the code...
            {/* Hidden password in the DOM */}
            <span style={{ display: "none" }} data-secret-password>
              🎉 You found the secret password! 🎉

              Anita, thank you for going on this little adventure with me. I made this whole web application just for you, and I hope you enjoyed every step of the puzzle. This is just the beginning of the experience I have planned for you. There’s so much more to come, and I can’t wait to see your reactions and share these moments together.

              You are my partner for life, my best friend, and my forever teammate. I can’t wait to do life with you, to create more memories, and to keep growing together. I love you more than anything in the world.

              And don't worry, this whole message isn't a replacement for a birthday card message :D

              — Your forever teammate, Marco

              P.S. The password is: {SECRET_PASSWORD}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordPrompt;